import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Smartphone, Trash2, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { useConfirm } from '@hooks/useConfirm';
import { useProfileData } from '@hooks/useProfileData';
import { useDateTimeFormat } from '@hooks/useDateTimeFormat';
import { api } from '@services/api';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import SettingsGroup from './SettingsGroup';
import DetailRow from './DetailRow';
import SessionsTable from './SessionsTable';

const FEEDBACK_TIMEOUT_MS = 3000;

const SecurityTab: React.FC = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const formatDate = useDateTimeFormat();
  const { mfaStatus, sessions, trustedDevices, loading, reload } = useProfileData();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const announce = useCallback((setter: (value: string | null) => void, message: string) => {
    setter(message);
    setTimeout(() => setter(null), FEEDBACK_TIMEOUT_MS);
  }, []);

  const revoke = useCallback(
    async (path: string, successKey: string, failureKey: string) => {
      try {
        await api.delete(path);
        announce(setSuccess, t(successKey));
        await reload();
      } catch (err) {
        announce(setError, resolveApiErrorMessage(t, err, { fallbackKey: failureKey }));
      }
    },
    [announce, reload, t],
  );

  return (
    <div className="flex flex-col gap-7">
      {error && (
        <Alert variant="destructive">
          <TriangleAlert className="size-4" strokeWidth={2} />
          <AlertDescription className="revy-cjk">{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success">
          <ShieldCheck className="size-4" strokeWidth={2} />
          <AlertDescription className="revy-cjk">{success}</AlertDescription>
        </Alert>
      )}

      <SettingsGroup
        boxAs="dl"
        title={t('profile.mfa')}
        description={t('settings.security.mfaDescription')}
      >
        {mfaStatus ? (
          <>
            <DetailRow label={t('profile.mfa')}>
              <Badge variant={mfaStatus.mfa_enabled ? 'default' : 'outline'} className="font-normal">
                {mfaStatus.mfa_enabled ? t('profile.mfaEnabled') : t('profile.mfaDisabled')}
              </Badge>
            </DetailRow>
            {mfaStatus.mfa_verified_at && (
              <DetailRow label={t('profile.lastVerified')}>
                <span className="revy-figure">{formatDate(mfaStatus.mfa_verified_at)}</span>
              </DetailRow>
            )}
            {mfaStatus.mfa_enabled && (
              <DetailRow label={t('profile.backupCodesRemaining')}>
                <span className="revy-figure">{mfaStatus.backup_codes_remaining}</span>
              </DetailRow>
            )}
          </>
        ) : (
          <DetailRow label={t('profile.mfa')}>
            <span className="text-muted-foreground">{t('profile.mfaNotAvailable')}</span>
          </DetailRow>
        )}
      </SettingsGroup>

      <SettingsGroup
        boxAs={false}
        title={t('profile.activeSessions')}
        description={t('settings.security.sessionsDescription')}
      >
        <SessionsTable
          sessions={sessions}
          loading={loading}
          onRevoke={async (id) => {
            if (
              !(await confirm({
                title: t('settings.security.signOutConfirm.title'),
                description: t('settings.security.signOutConfirm.description'),
                confirmLabel: t('settings.security.signOutConfirm.confirm'),
              }))
            ) {
              return;
            }
            await revoke(`/auth/sessions/${id}`, 'profile.sessionDeleted', 'profile.failedToDelete');
          }}
        />
      </SettingsGroup>

      {mfaStatus?.mfa_enabled && (
        <SettingsGroup
          boxAs="ul"
          title={t('profile.trustedDevices')}
          description={t('settings.security.trustedDevicesDescription')}
        >
          {trustedDevices.length > 0 ? (
            trustedDevices.map((device) => (
              <li key={device.id} className="flex min-h-[64px] items-center gap-3 px-4 py-3">
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
                  <Smartphone className="size-4" strokeWidth={1.9} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="revy-cjk truncate text-sm font-medium leading-[1.5]">
                    {device.device_name || t('profile.unnamedDevice')}
                  </p>
                  <p className="revy-figure text-xs text-muted-foreground">
                    {device.ip_address ?? '—'} · {formatDate(device.last_used_at)}
                  </p>
                </div>
                <TooltipIconButton
                  size="icon-sm"
                  label={t('profile.removeTrustedDevice')}
                  icon={<Trash2 strokeWidth={1.9} />}
                  onClick={async () => {
                    if (
                      !(await confirm({
                        title: t('settings.security.removeDeviceConfirm.title'),
                        description: t('settings.security.removeDeviceConfirm.description', {
                          deviceName: device.device_name || t('profile.unnamedDevice'),
                        }),
                        confirmLabel: t('settings.security.removeDeviceConfirm.confirm'),
                      }))
                    ) {
                      return;
                    }
                    await revoke(
                      `/auth/mfa/trusted-devices/${device.id}`,
                      'profile.deviceRemoved',
                      'profile.failedToRemove',
                    );
                  }}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                />
              </li>
            ))
          ) : (
            <li className="revy-cjk px-4 py-4 text-sm leading-[1.6] text-muted-foreground">
              {t('profile.noTrustedDevices')}
            </li>
          )}
        </SettingsGroup>
      )}
    </div>
  );
};

export default SecurityTab;
